import moment from "moment";
import React, { ReactElement, useEffect, useState } from "react";
import Avatar from "react-avatar";
import {
	ProjectAttachmentsDropzone,
	ProjectBoard,
	ProjectMember,
	useProjects,
} from ".";
import { LeadSelect } from "../leads";
import { Editable, Loading } from "../ui";
import { UserSelect, useUsers } from "../users";

import { faEye, faFileDownload } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	Card,
	Col,
	Container,
	FormControl,
	FormGroup,
	Row,
	Tab,
	Tabs,
} from "react-bootstrap";
import DatePicker from "react-datepicker";
import { RouteComponentProps } from "react-router";
import { toast } from "react-toastify";
import { AttachmentsListGroup } from "../attachments";

interface IParams {
	id?: string;
}

export const ProjectDashboardPage = (
	props: RouteComponentProps<IParams>,
): ReactElement => {
	const projectContext = useProjects();
	const userContext = useUsers();

	const [attachmentsTabKey, setAttachmentsTabKey] = useState("global");

	useEffect(() => {
		projectContext.find!(parseInt(props.match.params.id || "", 10));

		return function cleanup() {
			projectContext.setProject!(null);
		};
	}, []);

	if (!projectContext.project) {
		return <Loading />;
	}

	const project = projectContext.project;

	document.title = `Project Dashboard - ${project.name}`;

	const handleClickWatch = async (event: React.MouseEvent) => {
		event.stopPropagation();
		const proj = await projectContext.toggleWatchingProject!();

		if (userContext.user) {
			if (proj && proj.isWatchedBy(userContext.user)) {
				toast(
					"You are now watching this project, you will be notified when comments or changes are made to any task in this project.",
				);
			} else {
				toast("You are no longer watching this project.");
			}
		}
	};

	const titleField = (
		<Editable
			shownComponent={
				<h1>
					{project.name}
					{userContext.user ? (
						<FontAwesomeIcon
							className={
								project.isWatchedBy(userContext.user)
									? "active"
									: ""
							}
							icon={faEye}
							onClick={handleClickWatch}
							fixedWidth
						/>
					) : (
						""
					)}
				</h1>
			}
			editComponent={
				<FormGroup>
					<FormControl
						type="text"
						defaultValue={projectContext.project.name}
						onChange={(event: any) => {
							project.name = event.target.value;
						}}
						style={{
							fontSize: "2.5rem",
							marginTop: -12,
							marginLeft: -12,
						}}
					/>
				</FormGroup>
			}
			onFinishedEditing={() => {
				projectContext.save!();
			}}
		/>
	);

	const budgetField = (
		<Editable
			shownComponent={<strong>${project.budget}</strong>}
			editComponent={
				<FormGroup>
					<FormControl
						type="text"
						defaultValue={String(project.budget)}
						onChange={(event: any) => {
							project.budget = event.target.value;
						}}
					/>
				</FormGroup>
			}
			onFinishedEditing={() => {
				projectContext.save!();
			}}
		/>
	);

	const estimatedHoursField = (
		<Editable
			shownComponent={<strong>{project.estimatedHours}</strong>}
			editComponent={
				<FormGroup>
					<FormControl
						type="number"
						defaultValue={String(project.estimatedHours)}
						onChange={(event: any) => {
							project.estimatedHours = event.target.value;
						}}
					/>
				</FormGroup>
			}
			onFinishedEditing={() => {
				projectContext.save!();
			}}
		/>
	);

	const projectGroupField = (
		<Editable
			shownComponent={
				<strong style={{ display: "block", minHeight: 10 }}>
					{project.group}
				</strong>
			}
			editComponent={
				<FormGroup>
					<FormControl
						type="test"
						defaultValue={project.group}
						onChange={(event: any) => {
							project.group = event.target.value;
						}}
					/>
				</FormGroup>
			}
			onFinishedEditing={() => {
				projectContext.save!();
			}}
		/>
	);

	const projectStatusField = (
		<Editable
			shownComponent={<strong>{project.status}</strong>}
			editComponent={
				<FormGroup>
					<FormControl
						as="select"
						type="test"
						defaultValue={project.status}
						onChange={(event: any) => {
							project.status = event.target.value;
						}}
					>
						<option value="Not Started">Not Started</option>
						<option value="In Progress">In Progress</option>
						<option value="On Hold">On Hold</option>
						<option value="Internal">Internal</option>
						<option value="Support">Support</option>
						<option value="Completed">Completed</option>
					</FormControl>
				</FormGroup>
			}
			onFinishedEditing={() => {
				projectContext.save!();
			}}
		/>
	);

	const dueDateField = (
		<Editable
			shownComponent={
				<>
					<strong>
						{project.endDate
							? moment(project.endDate).format("YYYY-MM-DD")
							: "Not Set"}
					</strong>
				</>
			}
			editComponent={
				<FormGroup>
					<DatePicker
						className="form-control"
						selected={project.endDate}
						onChange={(date: Date) => {
							project.endDate = date;
							projectContext.save!();
						}}
					/>
				</FormGroup>
			}
			onFinishedEditing={() => {
				return;
			}}
		/>
	);

	const memberAvatars = project.members.map(
		(member: ProjectMember, index: number): any => {
			if (member.user) {
				return (
					<Avatar
						round
						key={index}
						name={member.user ? member.user.fullName : "NA"}
						size="30"
						color="#dfe1e6"
						fgColor="#172b4d"
						textSizeRatio={2}
						style={{ marginRight: 8 }}
					/>
				);
			}
		},
	);

	const memberEditField = (
		<Editable
			shownComponent={
				<>
					{memberAvatars}
					<Avatar
						round
						name="+"
						size="30"
						color="#dfe1e6"
						fgColor="#172b4d"
						textSizeRatio={2}
						style={{ marginRight: 8 }}
					/>
				</>
			}
			editComponent={
				<FormGroup>
					<UserSelect
						defaultValue={project.members.map(
							(member: ProjectMember) => {
								if (member.user) {
									return {
										value: member.user.id,
										label: member.user.fullName,
									};
								}
								return null;
							},
						)}
						handleChange={(_selected, type) => {
							let option = null;
							if (type.action === "remove-value") {
								option = type.removedValue;
							} else {
								option = type.option;
							}

							projectContext.saveMembers!(
								type.action,
								option.value,
								"user",
							);
						}}
					/>
				</FormGroup>
			}
			onFinishedEditing={() => {
				return;
			}}
		/>
	);

	const customerAvatars = project.members.map(
		(member: ProjectMember, index: number): any => {
			if (member.lead) {
				return (
					<Avatar
						round
						key={index}
						name={member.lead ? member.lead.fullName : "NA"}
						size="30"
						color="#dfe1e6"
						fgColor="#172b4d"
						textSizeRatio={2}
						style={{ marginRight: 8 }}
					/>
				);
			}
		},
	);

	const customerEditField = (
		<Editable
			shownComponent={
				<>
					{customerAvatars}
					<Avatar
						round
						name="+"
						size="30"
						color="#dfe1e6"
						fgColor="#172b4d"
						textSizeRatio={2}
						style={{ marginRight: 8 }}
					/>
				</>
			}
			editComponent={
				<FormGroup>
					<LeadSelect
						accountId={project.accountId}
						defaultValue={project.members.map(
							(member: ProjectMember) => {
								if (member.lead) {
									return {
										value: member.lead.id,
										label: member.lead.fullName,
									};
								}
								return null;
							},
						)}
						handleChange={(_selected, type) => {
							let option = null;
							if (type.action === "remove-value") {
								option = type.removedValue;
							} else {
								option = type.option;
							}

							projectContext.saveMembers!(
								type.action,
								option.value,
								"lead",
							);
						}}
					/>
				</FormGroup>
			}
			onFinishedEditing={() => {
				return;
			}}
		/>
	);

	return (
		<div id="project-dashboard">
			<Container className="project-details-container" fluid>
				<div className="title-wrapper">
					{titleField}
					{projectGroupField}
					<a href={`/accounts/${project.accountId}`}>
						Return to Account
					</a>
				</div>
				<Row>
					<Col>
						<Row className="project-details">
							<Col sm={4} className="mb-4">
								<Card
									className={`project-border-top-color-${
										project.color
									}`}
								>
									<Card.Body>
										<Card.Title>Status</Card.Title>
										{projectStatusField}
									</Card.Body>
								</Card>
							</Col>
							<Col sm={4} className="mb-4">
								<Card
									className={`project-border-top-color-${
										project.color
									}`}
								>
									<Card.Body>
										<Card.Title>Due Date</Card.Title>
										{dueDateField}
									</Card.Body>
								</Card>
							</Col>
							<Col sm={4} className="mb-4">
								<Card
									className={`project-border-top-color-${
										project.color
									}`}
								>
									<Card.Body>
										<Card.Title>Time Allocated</Card.Title>
										{estimatedHoursField}
									</Card.Body>
								</Card>
							</Col>
							<Col sm={4} className="mb-4">
								<Card
									className={`project-border-top-color-${
										project.color
									}`}
								>
									<Card.Body>
										<Card.Title>
											Time Logged
											<a
												href={
													project.timelogDownloadLink
												}
											>
												<FontAwesomeIcon
													icon={faFileDownload}
													style={{
														color: "#000",
														marginLeft: 4,
													}}
													size="1x"
													fixedWidth
												/>
											</a>
										</Card.Title>
										<strong>{project.timeLogged}</strong>
									</Card.Body>
								</Card>
							</Col>
							<Col sm={4} className="mb-4">
								<Card
									className={`project-border-top-color-${
										project.color
									}`}
								>
									<Card.Body>
										<Card.Title>Team</Card.Title>
										{memberEditField}
									</Card.Body>
								</Card>
							</Col>
							<Col sm={4} className="mb-4">
								<Card
									className={`project-border-top-color-${
										project.color
									}`}
								>
									<Card.Body>
										<Card.Title>Client</Card.Title>
										{customerEditField}
									</Card.Body>
								</Card>
							</Col>
							<Col sm={4} className="mb-4">
								<Card
									className={`project-border-top-color-${
										project.color
									}`}
								>
									<Card.Body>
										<Card.Title>Budget</Card.Title>
										{budgetField}
									</Card.Body>
								</Card>
							</Col>
							<Col sm={8} className="mb-4">
								<Card
									className={`project-border-top-color-${
										project.color
									}`}
								>
									<Card.Body>
										{project.opportunities.length ? (
											<Card.Title>
												Opportunities
											</Card.Title>
										) : (
											""
										)}

										<ul>
											{project.opportunities.map(
												(opp, index) => (
													<li key={index}>
														<a
															href={`/opportunities/${
																opp.id
															}`}
														>
															{opp.name} ($
															{opp.amount.toFixed(
																2,
															)}
															){" "}
														</a>
													</li>
												),
											)}
										</ul>
										{project.budgets.length ? (
											<Card.Title>Budgets</Card.Title>
										) : (
											""
										)}
										<ul>
											{project.budgets.map(
												(budget, index) => (
													<li key={index}>
														<a
															href={`/project-budget/${
																budget.id
															}`}
														>
															#{budget.id}{" "}
															{budget.name} ($
															{
																budget.amount
															}){" "}
														</a>
													</li>
												),
											)}
										</ul>
									</Card.Body>
								</Card>
							</Col>
						</Row>
					</Col>
					<Col>
						<Card
							className={`project-files mb-4 project-border-top-color-${
								project.color
							}`}
						>
							<Tabs
								id="files-tab"
								activeKey={attachmentsTabKey}
								onSelect={(k: any) => {
									setAttachmentsTabKey(String(k));
								}}
							>
								<Tab eventKey="global" title="Project Files">
									<ProjectAttachmentsDropzone />
								</Tab>
								<Tab eventKey="task" title="Task Files">
									<AttachmentsListGroup
										attachments={project.taskAttachments}
										folder={`projects/${project.id}/tasks`}
										appendAttachableId={true}
									/>
								</Tab>
							</Tabs>
						</Card>
					</Col>
				</Row>
			</Container>
			<Container
				style={{
					padding: 40,
					marginTop: 20,
				}}
				fluid
			>
				<h3 className="mb-4" style={{ marginLeft: 20 }}>
					Task Board
					<a href={project.tasksDownloadLink}>
						<FontAwesomeIcon
							icon={faFileDownload}
							style={{
								color: "#000",
								marginLeft: 4,
							}}
							size="1x"
							fixedWidth
						/>
					</a>
				</h3>
				<div>
					<ProjectBoard project={project} />
				</div>
			</Container>
		</div>
	);
};
